/**
 * Widget Service
 * Handles API calls for widget functionality
 */

class WidgetService {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '';
  }

  /**
   * Make API request with proper cache-busting
   */
  async makeRequest(endpoint, options = {}) {
    // Add cache-busting timestamp to URL
    const cacheBuster = `?t=${Date.now()}`;
    const url = `${this.baseURL}${endpoint}${cacheBuster}`;
    
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      cache: 'no-store', // Force no caching
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      const response = await fetch(url, finalOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      
      const data = await response.json();
      
      // Ensure we always return a consistent structure
      return {
        success: data.success !== undefined ? data.success : true,
        data: data.data || data
      };
    } catch (error) {
      console.error(`[WidgetService] Request failed:`, error);
      throw error;
    }
  }

  /**
   * Get agency configuration for widget
   */
  async getAgencyConfig(agencyId) {
    try {
      return await this.makeRequest(`/api/widget/config/${agencyId}`);
    } catch (error) {
      console.error('[WidgetService] getAgencyConfig failed:', error);
      // Return safe default structure
      return {
        success: false,
        data: {
          title: 'Request a Quote',
          primaryColor: '#3B82F6',
          showAgentSelection: true,
          agencyName: 'Your Travel Agency',
          agencyLogo: null
        },
        error: error.message
      };
    }
  }

  /**
   * Get agents list for agency
   */
  async getAgencyAgents(agencyId) {
    try {
      return await this.makeRequest(`/api/widget/agents/${agencyId}`);
    } catch (error) {
      console.error('[WidgetService] getAgencyAgents failed:', error);
      // Return safe default structure
      return {
        success: false,
        data: [],
        error: error.message
      };
    }
  }

  /**
   * Submit lead capture form
   */
  async submitLead(leadData) {
    try {
      return await this.makeRequest('/api/widget/leads', {
        method: 'POST',
        body: JSON.stringify(leadData)
      });
    } catch (error) {
      console.error('[WidgetService] submitLead failed:', error);
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }
}

// Export singleton instance
export default new WidgetService();
